import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Box, Spinner, Text } from '@chakra-ui/react';
import { DataTable, TableOptions, type Column } from '@themes/aegix';
import { useQuery } from '@tanstack/react-query';
import {
  listLinkableSamlConfigs,
  listScimConfigs,
  type SamlConfigSummary,
  type ScimConfigRow,
  type ScimIdpType,
} from '../../services';
import { SamlConfigDrawer } from './sso/SamlConfigDrawer';
import { ScimConfigDrawer } from './sso/ScimConfigDrawer';

type SsoRowKind = 'scim' | 'saml';
type SsoRowStatus = 'healthy' | 'failed' | 'pending' | 'disabled' | 'active';

interface SsoRow {
  rowKey: string;
  kind: SsoRowKind;
  id: number;
  name: string;
  app_name: string | null;
  type: string;
  status: SsoRowStatus;
  last_sync: string | null;
}

// The wire protocol each IdP speaks, which is not the vendor name: Okta and
// Entra push over SCIM, while Google and Duo are polled over their own admin
// APIs. Mirrors PUSH_IDP_TYPES / PULL_IDP_TYPES in ScimConfigDrawer.
const PROTOCOL_BY_IDP_TYPE: Record<ScimIdpType, string> = {
  okta: 'SCIM',
  azure: 'SCIM',
  google: 'GOOGLE',
  duo: 'DUO',
};

function scimToRow(c: ScimConfigRow): SsoRow {
  let status: SsoRowStatus = 'pending';
  if (!c.enabled) status = 'disabled';
  else if (c.sync_status === 'success') status = 'healthy';
  else if (c.sync_status === 'failed') status = 'failed';
  return {
    rowKey: `scim:${c.id}`,
    kind: 'scim',
    id: c.id,
    name: c.idp_name,
    app_name: c.app_name,
    type: PROTOCOL_BY_IDP_TYPE[c.idp_type] ?? c.idp_type.toUpperCase(),
    status,
    last_sync: c.last_sync,
  };
}

function samlToRow(c: SamlConfigSummary): SsoRow {
  return {
    rowKey: `saml:${c.id}`,
    kind: 'saml',
    id: c.id,
    name: c.name,
    app_name: c.app_name,
    type: (c.type ?? 'saml').toUpperCase(),
    status: 'active',
    last_sync: null,
  };
}

function StatusBadge({ status, t }: { status: SsoRowStatus; t: (key: string, fallback: string) => string }) {
  switch (status) {
    case 'healthy':
      return <Badge colorPalette="green">{t('sso.statusHealthy', 'Healthy')}</Badge>;
    case 'failed':
      return <Badge colorPalette="red">{t('sso.statusFailed', 'Failed')}</Badge>;
    case 'pending':
      return <Badge colorPalette="yellow">{t('sso.statusPending', 'Pending')}</Badge>;
    case 'disabled':
      return <Badge colorPalette="gray">{t('common.disabled', 'Disabled')}</Badge>;
    case 'active':
    default:
      return <Badge colorPalette="green">{t('common.active', 'Active')}</Badge>;
  }
}

function KindBadge({ kind, t }: { kind: SsoRowKind; t: (key: string, fallback: string) => string }) {
  return (
    <Badge colorPalette={kind === 'saml' ? 'purple' : 'blue'}>
      {kind === 'saml' ? t('sso.signIn', 'Sign-in') : t('sso.sync', 'Sync')}
    </Badge>
  );
}

interface DrawerState {
  kind: SsoRowKind | null;
  configId: number | null;
}

export const SsoConfig = () => {
  const { t } = useTranslation();
  const [drawer, setDrawer] = useState<DrawerState>({
    kind: null,
    configId: null,
  });

  const typeFilterOptions = useMemo(
    () => [
      { label: t('common.all', 'All'), value: 'all' },
      { label: t('sso.signIn', 'Sign-in'), value: 'saml' },
      { label: t('sso.sync', 'Sync'), value: 'scim' },
    ],
    [t],
  );

  const scimQuery = useQuery({
    queryKey: ['sso', 'scim-configs'],
    queryFn: listScimConfigs,
  });
  const samlQuery = useQuery({
    queryKey: ['sso', 'saml-configs'],
    queryFn: listLinkableSamlConfigs,
  });

  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [kindFilter, setKindFilter] = useState('all');
  const [searchValue, setSearchValue] = useState('');

  const rows = useMemo<SsoRow[]>(() => {
    const scimRows = (scimQuery.data ?? []).map(scimToRow);
    const samlRows = (samlQuery.data ?? []).map(samlToRow);
    const all = [...samlRows, ...scimRows];
    // 'all' → no kind filter, else match kind. The toolbar toggle and the
    // Type-column filter drive this one state, so they can't disagree.
    const byKind =
      kindFilter === 'all' ? all : all.filter((r) => r.kind === kindFilter);
    const needle = searchValue.trim().toLowerCase();
    if (!needle) return byKind;
    return byKind.filter((r) =>
      [r.name, r.type, r.app_name ?? ''].some((field) =>
        field.toLowerCase().includes(needle),
      ),
    );
  }, [scimQuery.data, samlQuery.data, filterValues, kindFilter, searchValue]);

  const columns: Column<SsoRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('common.name', 'Name'),
        sortable: true,
        filterable: true,
        width: '240px',
      },
      {
        key: 'kind',
        label: t('common.type', 'Type'),
        width: '120px',
        filterable: true,
        filterOptions: typeFilterOptions,
        render: (row) => <KindBadge kind={row.kind} t={t} />,
      },
      {
        key: 'type',
        label: t('sso.protocol', 'Protocol'),
        sortable: true,
        width: '140px',
      },
      {
        key: 'app_name',
        label: t('sso.app', 'App'),
        sortable: true,
        width: '160px',
        render: (row) =>
          row.app_name ?? (
            <Text fontSize="sm" color="gray.500">
              —
            </Text>
          ),
      },
      {
        key: 'status',
        label: t('common.status', 'Status'),
        width: '140px',
        render: (row) => <StatusBadge status={row.status} t={t} />,
      },
      {
        key: 'last_sync',
        label: t('sso.lastSync', 'Last sync'),
        width: '200px',
        render: (row) =>
          row.last_sync ? (
            new Date(row.last_sync).toLocaleString()
          ) : (
            <Text fontSize="sm" color="gray.500">
              —
            </Text>
          ),
      },
    ],
    [t, typeFilterOptions],
  );

  const openCreate = (kind: SsoRowKind) => {
    setDrawer({ kind, configId: null });
  };

  const openEdit = (row: SsoRow) => {
    setDrawer({ kind: row.kind, configId: row.id });
  };

  const closeDrawer = () => {
    setDrawer({ kind: null, configId: null });
  };

  const isLoading = scimQuery.isLoading || samlQuery.isLoading;
  const isError = scimQuery.isError || samlQuery.isError;

  return (
    <div>
      <TableOptions
        toggleOptions={typeFilterOptions}
        activeToggle={kindFilter}
        onToggleChange={setKindFilter}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder={t('common.searchPlaceholder', 'Search...')}
        primaryActionMenu={{
          label: t('sso.addConfig', 'Add Config'),
          'data-e2e': 'sso-add-config',
          items: [
            {
              label: t('sso.signIn', 'Sign-in'),
              'data-e2e': 'sso-add-saml',
              onClick: () => openCreate('saml'),
            },
            {
              label: t('sso.sync', 'Sync'),
              'data-e2e': 'sso-add-scim',
              onClick: () => openCreate('scim'),
            },
          ],
        }}
      />
      <Box px={5} pb={5}>
        {isLoading ? (
          <Box py={8}>
            <Spinner /> <Text ml={3}>{t('sso.loadingConfigs', 'Loading configurations…')}</Text>
          </Box>
        ) : isError ? (
          <Text color="red.600">
            {t('sso.failedLoadConfigs', 'Failed to load SSO configurations')}:{' '}
            {(scimQuery.error ?? samlQuery.error)?.toString()}
          </Text>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            keyExtractor={(row) => row.rowKey}
            onRowClick={openEdit}
            filterValues={{ ...filterValues, kind: kindFilter }}
            onFilterChange={(column, value) => {
              if (column === 'kind') {
                setKindFilter(value || 'all');
                return;
              }
              setFilterValues((f) => ({ ...f, [column]: value }));
            }}
            onFilterClear={(column) => {
              if (column === 'kind') {
                setKindFilter('all');
                return;
              }
              setFilterValues(({ [column]: _, ...rest }) => rest);
            }}
          />
        )}
      </Box>

      <SamlConfigDrawer
        isOpen={drawer.kind === 'saml'}
        onClose={closeDrawer}
        configId={drawer.kind === 'saml' ? drawer.configId : null}
      />
      <ScimConfigDrawer
        isOpen={drawer.kind === 'scim'}
        onClose={closeDrawer}
        configId={drawer.kind === 'scim' ? drawer.configId : null}
      />
    </div>
  );
};
